const API_BASE_URL = '/api';

export class ApiService {
  static async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const response = await fetch(url, { ...defaultOptions, ...options });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erreur de connexion' }));
      throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
    }

    return response.json();
  }

  // Sessions
  static async createSession(name: string, teacherId: string) {
    return this.request<{ success: boolean; session: { id: string; code: string; name: string; status: string; createdAt: string } }>('/sessions', {
      method: 'POST',
      body: JSON.stringify({ name, teacherId }),
    });
  }

  // Partage de sessions
  static async shareSession(sessionId: string, teacherEmail?: string, teacherId?: string) {
    return this.request<{ success: boolean; message: string; sharedWith: any[] }>(`/sessions/${sessionId}/share`, {
      method: 'POST',
      body: JSON.stringify({ teacherEmail, teacherId }),
    });
  }

  static async unshareSession(sessionId: string, teacherId: string) {
    return this.request<{ success: boolean; message: string; sharedWith: any[] }>(`/sessions/${sessionId}/share/${teacherId}`, {
      method: 'DELETE',
    });
  }

  static async getSessionSharedWith(sessionId: string) {
    return this.request<{ success: boolean; sharedWith: any[]; ownerId: string }>(`/sessions/${sessionId}/shared-with`);
  }

  static async searchTeachers(query: string) {
    return this.request<{ success: boolean; teachers: Array<{ id: string; name: string; email: string }> }>(`/sessions/teachers/search?query=${encodeURIComponent(query)}`);
  }

  static async joinSession(code: string, studentId: string, studentName: string) {
    return this.request<{ success: boolean; session: { id: string; code: string; name: string; status: string; currentQuestion: string | null; participantsCount: number } }>('/sessions/join', {
      method: 'POST',
      body: JSON.stringify({ code, studentId, studentName }),
    });
  }

  static async getSession(sessionId: string) {
    return this.request<{ success: boolean; session: any }>(`/sessions/${sessionId}`);
  }

  static async getSessionByCode(code: string) {
    return this.request<{ success: boolean; session: any }>(`/sessions/code/${code}`);
  }

  static async getTeacherSessions(teacherId: string) {
    return this.request<{ success: boolean; sessions: any[] }>(`/sessions/teacher/${teacherId}`);
  }

  static async updateSessionStatus(sessionId: string, status: string) {
    return this.request<{ success: boolean; session: { id: string; status: string } }>(`/sessions/${sessionId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  static async deleteSession(sessionId: string) {
    return this.request<{ success: boolean; message: string }>(`/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  }

  static async exportSessionResults(sessionId: string): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/export`);
    if (!response.ok) {
      throw new Error('Erreur lors de l\'export des résultats');
    }
    return response.blob();
  }

  // Questions
  static async createQuestion(questionData: {
    sessionId: string;
    type: string;
    title: string;
    content: string;
    options?: any[];
    correctAnswer?: string;
    timeLimit?: number;
  }) {
    return this.request<{ success: boolean; question: any }>('/questions', {
      method: 'POST',
      body: JSON.stringify(questionData),
    });
  }

  static async getQuestion(questionId: string) {
    return this.request<{ success: boolean; question: any }>(`/questions/${questionId}`);
  }

  static async updateQuestion(questionId: string, questionData: {
    title: string;
    content: string;
    type: string;
    options?: any[];
    ratingScale?: {
      min: number;
      max: number;
      labels?: string[];
    };
    correctAnswer?: string;
    timeLimit?: number;
  }) {
    return this.request<{ success: boolean; question: any }>(`/questions/${questionId}`, {
      method: 'PATCH',
      body: JSON.stringify(questionData),
    });
  }

  static async activateQuestion(questionId: string) {
    return this.request<{ success: boolean; question: any }>(`/questions/${questionId}/activate`, {
      method: 'PATCH',
    });
  }

  static async submitAnswer(questionId: string, studentId: string, studentName: string, answer: string) {
    return this.request<{ success: boolean; message: string }>(`/questions/${questionId}/respond`, {
      method: 'POST',
      body: JSON.stringify({ studentId, studentName, answer }),
    });
  }

  static async getQuestionResponses(questionId: string) {
    return this.request<{ success: boolean; responses: any[]; totalResponses: number }>(`/questions/${questionId}/responses`);
  }

  static async getSessionQuestions(sessionId: string) {
    return this.request<{ success: boolean; questions: any[] }>(`/questions/session/${sessionId}`);
  }

  static async deactivateAllQuestions(sessionId: string) {
    return this.request<{ success: boolean; message: string }>(`/questions/session/${sessionId}/deactivate-all`, {
      method: 'PATCH',
    });
  }

  // Importer des questions depuis un fichier JSON
  static async importQuestions(sessionId: string, file: File) {
    const formData = new FormData();
    formData.append('questionsFile', file);
    
    return fetch(`${API_BASE_URL}/questions/import/${sessionId}`, {
      method: 'POST',
      body: formData,
    }).then(response => {
      if (!response.ok) {
        return response.json().then(err => Promise.reject(err));
      }
      return response.json();
    });
  }

  // Télécharger le modèle de fichier JSON
  static async downloadTemplate() {
    try {
      // Utiliser l'URL directe du backend pour éviter les problèmes de proxy
      const backendUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001';
      const fullUrl = `${backendUrl}/api/questions/template`;
      
      console.log('📥 Téléchargement du modèle depuis:', fullUrl);
      
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      // Vérifier le type de contenu
      const contentType = response.headers.get('content-type');
      console.log('📋 Content-Type reçu:', contentType);
      
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(`Type de contenu incorrect: ${contentType}`);
      }
      
      // Récupérer le JSON
      const jsonData = await response.json();
      console.log('✅ JSON reçu:', jsonData);
      
      // Créer un blob JSON
      const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
        type: 'application/json'
      });
      
      // Télécharger le fichier
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ciar-questions-template.json';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      console.log('✅ Téléchargement réussi');
      
    } catch (error) {
      console.error('❌ Erreur lors du téléchargement:', error);
      throw error;
    }
  }

  // Supprimer une question
  static async deleteQuestion(questionId: string) {
    return this.request<{ success: boolean; message: string }>(`/questions/${questionId}`, {
      method: 'DELETE',
    });
  }

  // Exporter les questions d'une session
  static async exportQuestions(sessionId: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/questions/export/${sessionId}`);
      if (!response.ok) {
        throw new Error('Erreur lors de l\'export des questions');
      }
      return response.blob();
    } catch (error) {
      console.error('Erreur export questions:', error);
      throw error;
    }
  }

  // Marquer une session comme autonome
  static async setSessionAutonomous(sessionId: string, isAutonomous: boolean) {
    return this.request<{ success: boolean; session: any }>(`/sessions/${sessionId}/autonomous`, {
      method: 'PATCH',
      body: JSON.stringify({ isAutonomous }),
    });
  }

  // Modifier le statut du timer d'une session
  static async setSessionTimer(sessionId: string, disableTimer: boolean) {
    return this.request<{ success: boolean; session: any }>(`/sessions/${sessionId}/timer`, {
      method: 'PATCH',
      body: JSON.stringify({ disableTimer }),
    });
  }

  static async getStudentResponses(sessionId: string, studentId: string) {
    return this.request<{ 
      success: boolean; 
      answeredQuestionIds: string[]; 
      totalQuestions: number; 
      answeredCount: number; 
    }>(`/sessions/${sessionId}/student/${studentId}/responses`);
  }

  // Health check
  static async healthCheck() {
    return this.request<{ status: string; message: string; timestamp: string }>('/health');
  }
}
